import React, { useState, useEffect, useRef } from 'react';
import LeftSidebar from '../Tools/LeftSidebar';
import Topbar from '../Tools/Topbar';
import Settings from '../Tools/Setting/Settings';
import useSettings from '../Tools/Setting/useSettings';
import ResourceAreaChart from './Chart/Overview/ResourceAreaChart';
import SpecialityAreaChart from './Chart/Overview/SpecialityAreaChart';
import EmployeeAssetUsageChart from './Chart/Overview/EmployeeAssetUsageChart';
import axiosInstance from '../../../api';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas'; 
import { create } from 'xmlbuilder2';
import Pagination from '../Tools/Pagination';
import { format, parseISO } from 'date-fns';
import autoTable from 'jspdf-autotable';
import ResourceStatusHistory from './Reports&Analytics/ResourceStatusHistory';

// Component to fetch and store date and time formats in sessionStorage
const DateTimeFormatFetcher = () => {
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axiosInstance.get('/ems/get-system-setting', {});
        if (res.data?.status === 'success' && res.data.data.length > 0) {
          const data = res.data.data[0];
          sessionStorage.setItem('date_format', data.date_format || 'DD/MM/YYYY');
          sessionStorage.setItem('time_format', data.time_format || '24');
        }
      } catch (err) {
        console.error('Error fetching date and time formats:', err);
        sessionStorage.setItem('date_format', 'DD/MM/YYYY');
        sessionStorage.setItem('time_format', '24');
      }
    };
    fetchSettings();
  }, []);

  return null;
};

const EMSReportsAnalytics = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const { settings, updateSetting, handleColorTheme } = useSettings();
  const settingsRef = useRef();

  // State for Resource Activity Logs
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [filtersActivity, setFiltersActivity] = useState({ datetime: '' });
  const [pageActivity, setPageActivity] = useState(1);
  const limitActivity = 10;

  // State for Alert History
  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [filtersAlerts, setFiltersAlerts] = useState({ type: '', datetime: '' });
  const [pageAlerts, setPageAlerts] = useState(1);
  const limitAlerts = 10;

  // State for Resource Status History
  const [resourceStatusHistory, setResourceStatusHistory] = useState([]);
  const [loadingResourceStatus, setLoadingResourceStatus] = useState(false);
  const [filtersResourceStatus, setFiltersResourceStatus] = useState({
    status: '',
    datetime: '',
    resource_name: '',
  });
  const [pageResourceStatus, setPageResourceStatus] = useState(1);
  const limitResourceStatus = 10;

  // State for Accepted Cases Overview
  const [acceptedCases, setAcceptedCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);
  const [filtersCases, setFiltersCases] = useState({ ems_team_status: '', date: '' });
  const [pageCases, setPageCases] = useState(1);
  const limitCases = 10;

  // State for Accepted Transfer Cases
  const [acceptedTransferCases, setAcceptedTransferCases] = useState([]);
  const [loadingTransferCases, setLoadingTransferCases] = useState(false);
  const [filtersTransferCases, setFiltersTransferCases] = useState({ ems_team_status: '', date: '' });
  const [pageTransferCases, setPageTransferCases] = useState(1);
  const limitTransferCases = 10;

  // State for Rejected Cases Overview
  const [rejectedCases, setRejectedCases] = useState([]);
  const [loadingRejectedCases, setLoadingRejectedCases] = useState(false);
  const [filtersRejectedCases, setFiltersRejectedCases] = useState({ ems_team_status: '', date: '' });
  const [pageRejectedCases, setPageRejectedCases] = useState(1);
  const limitRejectedCases = 10;

  // State for Hold Transfer Cases
  const [holdTransferCases, setHoldTransferCases] = useState([]);
  const [loadingHoldTransferCases, setLoadingHoldTransferCases] = useState(false);
  const [filtersHoldTransferCases, setFiltersHoldTransferCases] = useState({ ems_team_status: '', date: '' });
  const [pageHoldTransferCases, setPageHoldTransferCases] = useState(1);
  const limitHoldTransferCases = 10;

  // State for Capacity Usage Per Time
  const [capacityUsage, setCapacityUsage] = useState([]);
  const [loadingCapacity, setLoadingCapacity] = useState(false);
  const [filtersCapacity, setFiltersCapacity] = useState({ ems_team_status: '', date: '' });
  const [pageCapacity, setPageCapacity] = useState(1);
  const limitCapacity = 10;

  // Get date and time format from sessionStorage
const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
const timeFormat = sessionStorage.getItem('time_format') || '24';

// Robust UTC-aware formatting (matches CasesTab exactly)
const formatCaseDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const dateFormatMap = {
      'DD/MM/YYYY': 'dd/MM/yyyy',
      'MM/DD/YYYY': 'MM/dd/yyyy',
      'YYYY-MM-DD': 'yyyy-MM-dd',
    };
    const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
    const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;

    let utcDate;
    if (dateString.includes('T')) {
      utcDate = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
    } else {
      utcDate = new Date(dateString.replace(' ', 'T') + 'Z');
    }

    const utcTimestamp = new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
      utcDate.getUTCHours(),
      utcDate.getUTCMinutes(),
      utcDate.getUTCSeconds()
    );

    return format(utcTimestamp, formatString);
  } catch (err) {
    console.error('Date formatting error:', err, dateString);
    return 'Invalid Date';
  }
};

const formatCaseDateOnly = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const dateFormatMap = {
      'DD/MM/YYYY': 'dd/MM/yyyy',
      'MM/DD/YYYY': 'MM/dd/yyyy',
      'YYYY-MM-DD': 'yyyy-MM-dd',
    };

    let utcDate;
    if (dateString.includes('T')) {
      utcDate = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
    } else {
      utcDate = new Date(dateString.replace(' ', 'T') + 'Z');
    }

    const utcTimestamp = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
    return format(utcTimestamp, dateFormatMap[dateFormat] || 'dd/MM/yyyy');
  } catch (err) {
    console.error('Date only formatting error:', err);
    return 'N/A';
  }
};

  // Utility function to process Urdu/Arabic text
  const processUrduText = (text) => {
    if (!text) return 'N/A';
    let processedText = text.toString();
    processedText = processedText
      .replace(/[^\x20-\x7E\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return processedText || 'N/A';
  };

  // Original exportToPDF function for other sections
  const exportToPDF = async (data, title, headers, fields, filename) => {
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const margin = 10;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(title, pageWidth / 2, margin + 10, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const currentDate = new Date();
      const infoLines = [
        `Generated on: ${formatCaseDateTime(currentDate.toISOString())}`,
      ];
      let startY = margin + 20;
      infoLines.forEach((line) => {
        doc.text(line, margin, startY, { align: 'left' });
        startY += 5;
      });

      const formattedBody = data.map((item) => {
        return fields.map((field) => {
          let value = '';
          if (field === 'user') {
            value = `${item[field]?.first_name || ''} ${item[field]?.last_name || ''}`.trim();
          } else if (field === 'created_at' || field === 'date') {
            value = item[field] ? (field === 'date' ? formatCaseDateOnly(item[field]) : formatCaseDateTime(item[field])) : 'N/A';
          } else {
            value = processUrduText(item[field] || 'N/A');
          }
          return value;
        });
      });

      const columnStyles = {
        0: { halign: 'left', cellWidth: 50 },
        1: { halign: 'left', cellWidth: 50 },
        2: { halign: 'center', cellWidth: 50 },
        3: { halign: 'center', cellWidth: 40 },
      };

      autoTable(doc, {
        startY: startY + 10,
        head: [headers],
        body: formattedBody,
        theme: 'grid',
        headStyles: {
          fillColor: [0, 123, 255],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 10,
          font: 'helvetica',
          halign: 'center',
        },
        bodyStyles: {
          fontSize: 10,
          font: 'helvetica',
          textColor: 0,
        },
        columnStyles: columnStyles,
        styles: {
          cellPadding: 3,
          overflow: 'linebreak',
          font: 'helvetica',
        },
        didDrawPage: (data) => {
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.text(
            `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`,
            pageWidth - margin - 20,
            pageHeight - margin,
            { align: 'right' }
          );
        },
      });

    doc.save(`${filename}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // New exportResourceStatusToPDF function using html2canvas
  const exportResourceStatusToPDF = async () => {
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const margin = 10;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Resource Status History', pageWidth / 2, margin + 10, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const currentDate = new Date();
      const infoLines = [
        `Generated on: ${formatCaseDateTime(currentDate.toISOString())}`,
      ];
      let startY = margin + 20;
      infoLines.forEach((line) => {
        doc.text(line, margin, startY, { align: 'left' });
        startY += 5;
      });

      // Capture the table using html2canvas
      const tableElement = document.querySelector('.resource-status-history-table');
      if (!tableElement) {
        throw new Error('Table element not found');
      }

      const canvas = await html2canvas(tableElement, {
        scale: 2, // Increase scale for better resolution
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pageWidth - 2 * margin;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Check if image fits on the current page
      if (startY + imgHeight > pageHeight - margin) {
        doc.addPage();
        startY = margin;
      }

      doc.addImage(imgData, 'PNG', margin, startY, imgWidth, imgHeight);
      startY += imgHeight + 10;

      // Add page number
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Page 1 of ${doc.internal.getNumberOfPages()}`,
        pageWidth - margin - 20,
        pageHeight - margin,
        { align: 'right' }
      );

      doc.save('resource_status_history.pdf');
    } catch (error) {
      console.error('Error generating Resource Status PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Export to XML function
  const exportToXML = (data, rootElement, rowElement, fields, filename) => {
    const xmlObj = {
      [rootElement]: {
        [rowElement]: data.map((item) => {
          const row = {};
          fields.forEach((field) => {
            let value = '';
            if (field === 'user') {
              value = `${item[field]?.first_name || ''} ${item[field]?.last_name || ''}`;
            } else if (field === 'created_at' || field === 'date') {
              value = item[field] ? (field === 'date' ? formatCaseDateOnly(item[field]) : formatCaseDateTime(item[field])) : 'N/A';
            } else {
              value = processUrduText(item[field] || 'N/A');
            }
            row[field] = value;
          });
          return row;
        }),
      },
    };

    const xml = create({ version: '1.0', encoding: 'UTF-8' }).ele(xmlObj).end({ prettyPrint: true });
    const blob = new Blob([xml], { type: 'application/xml; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.xml`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Export to CSV function
  const exportToCSV = (data, headers, fields, filename) => {
    const escapeCsvValue = (value) => {
      if (value == null) return '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const csvRows = [
      '\uFEFF' + headers.join(','),
      ...data.map((item) =>
        fields.map((field) => {
          let value = '';
          if (field === 'user') {
            value = `${item[field]?.first_name || ''} ${item[field]?.last_name || ''}`;
          } else if (field === 'created_at' || field === 'date') {
            value = item[field] ? (field === 'date' ? formatCaseDateOnly(item[field]) : formatCaseDateTime(item[field])) : 'N/A';
          } else {
            value = processUrduText(item[field] || 'N/A');
          }
          return escapeCsvValue(value);
        }).join(',')
      ),
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Export handler
  const handleExport = (section, format) => {
    const exportData = {
      activityLogs: {
        data: activityLogs,
        title: 'Resource Activity Logs',
        headers: ['Resource Name', 'Action Performed', 'Performed By', 'Timestamp'],
        fields: ['resource_name', 'action_performed', 'performed_by', 'created_at'],
        filename: 'resource_activity_logs',
      },
      resourceStatusHistory: {
      data: resourceStatusHistory,
      title: 'Resource Status History',
      headers: ['Resource Name', 'Previous Status → New Status', 'Date Changed', 'Changed By'],
      fields: ['resource_name', 'previous_and_new_status', 'created_at', 'user'],
      filename: 'resource_status_history',
    },
      alerts: {
        data: alerts,
        title: 'Alert History',
        headers: ['Alert Type', 'Resource Affected', 'Severity', 'Date & Time'],
        fields: ['module_name', 'description', 'severity', 'created_at'],
        filename: 'alert_history',
      },
      acceptedCases: {
        data: acceptedCases,
        title: 'Accepted Cases Overview',
        headers: ['Case ID', 'Case Name', 'Assigned Date', 'Status'],
        fields: ['id', 'case_name', 'created_at', 'ems_team_status'],
        filename: 'accepted_cases',
      },
      rejectedCases: {
        data: rejectedCases,
        title: 'Hold Cases Overview',
        headers: ['Case ID', 'Case Name', 'Assigned Date', 'Status'],
        fields: ['id', 'case_name', 'created_at', 'ems_team_status'],
        filename: 'rejected_cases',
      },
      acceptedTransferCases: {
        data: acceptedTransferCases,
        title: 'Accepted Transfer Cases',
        headers: ['Case ID', 'Case Name', 'Assigned Date', 'Status'],
        fields: ['id', 'case_name', 'created_at', 'ems_team_status'],
        filename: 'accepted_transfer_cases',
      },
      holdTransferCases: {
        data: holdTransferCases,
        title: 'Hold Transfer Cases',
        headers: ['Case ID', 'Case Name', 'Assigned Date', 'Status'],
        fields: ['id', 'case_name', 'created_at', 'transfer_status'],
        filename: 'hold_transfer_cases',
      },
      capacityUsage: {
        data: capacityUsage,
        title: 'Capacity Usage Per Time',
        headers: ['Date', 'Employee Usage (%)', 'Vehicle Usage (%)'],
        fields: ['date', 'employee_usage', 'vehicle_usage'],
        filename: 'capacity_usage',
      },
    };

    const sectionData = exportData[section];
    if (!sectionData || !sectionData.data || sectionData.data.length === 0) {
      console.warn(`No data available for export in section: ${section}`);
      alert('No data available to export.');
      return;
    }

    switch (format) {
      case 'pdf':
        if (section === 'resourceStatusHistory') {
          exportResourceStatusToPDF();
        } else {
          exportToPDF(
            sectionData.data,
            sectionData.title,
            sectionData.headers,
            sectionData.fields,
            sectionData.filename
          );
        }
        break;
      case 'xml':
        exportToXML(
          sectionData.data,
          `${sectionData.title.replace(/\s/g, '')}`,
          sectionData.title.replace(/\s/g, '') + 'Entry',
          sectionData.fields,
          sectionData.filename
        );
        break;
      case 'csv':
        exportToCSV(
          sectionData.data,
          sectionData.headers,
          sectionData.fields,
          sectionData.filename
        );
        break;
      default:
        console.warn(`Unsupported export format: ${format} for section: ${section}`);
        break;
    }
  };

  // Fetch capacity usage data
  const fetchCapacityUsage = async () => {
    try {
      setLoadingCapacity(true);
      const response = await axiosInstance.get('/ems/capacity-usage-per-time', {
        params: {
          ems_team_status: filtersCapacity.ems_team_status,
          date: filtersCapacity.date,
        },
      });

      const apiData = Array.isArray(response.data) ? response.data : response.data?.data;
      setCapacityUsage(apiData || []);
    } catch (error) {
      console.error('Error fetching capacity usage:', error);
    } finally {
      setLoadingCapacity(false);
    }
  };

  // Fetch activity logs
  const fetchActivityLogs = async () => {
    try {
      setLoadingActivity(true);
      const response = await axiosInstance.get('/ems/resource-activity-logs', {
        params: {
          user_id: 68,
          datetime: filtersActivity.datetime,
        },
        headers: {
          'Accept': 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
          Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
        },
      });
      
      const apiData = Array.isArray(response.data) ? response.data : response.data?.data;
      setActivityLogs(apiData.filter((log) => log.resource_name && log.action_performed) || []);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setLoadingActivity(false);
    }
  };

  // Fetch alert history
  const fetchAlerts = async () => {
    try {
      setLoadingAlerts(true);
      const response = await axiosInstance.get('/ems/alert-history', {
        params: {
          type: filtersAlerts.type,
          datetime: filtersAlerts.datetime,
        },
      });

      const apiData = Array.isArray(response.data) ? response.data : response.data?.data;
      setAlerts(apiData || []);
    } catch (error) {
      console.error('Error fetching alert history:', error);
    } finally {
      setLoadingAlerts(false);
    }
  };

  // Fetch resource status history
  const fetchResourceStatusHistory = async () => {
    try {
      setLoadingResourceStatus(true);
      const response = await axiosInstance.get('/ems/resource-status-history', {
        params: {
          user_id: 68,
          status: filtersResourceStatus.status,
          datetime: filtersResourceStatus.datetime,
          resource_name: filtersResourceStatus.resource_name,
        },
      });

      const apiData = Array.isArray(response.data) ? response.data : response.data?.data;
      setResourceStatusHistory(apiData || []);
    } catch (error) {
      console.error('Error fetching resource status history:', error);
    } finally {
      setLoadingResourceStatus(false);
    }
  };

  // Fetch accepted cases
  const fetchAcceptedCases = async () => {
    try {
      setLoadingCases(true);
      const response = await axiosInstance.get('/ems/accepted-cases-overview', {
        params: {
          ems_team_status: filtersCases.ems_team_status,
          date: filtersCases.date,
        },
      });

      const apiData = Array.isArray(response.data) ? response.data : response.data?.data;
      setAcceptedCases(apiData || []);
    } catch (error) {
      console.error('Error fetching accepted cases:', error);
    } finally {
      setLoadingCases(false);
    }
  };

  // Fetch rejected cases
  const fetchRejectedCases = async () => {
    try {
      setLoadingRejectedCases(true);
      const response = await axiosInstance.get('/ems/rejected-cases-overview', {
        params: {
          ems_team_status: filtersRejectedCases.ems_team_status,
          date: filtersRejectedCases.date,
        },
      });

      const apiData = Array.isArray(response.data) ? response.data : response.data?.data;
      setRejectedCases(apiData || []);
    } catch (error) {
      console.error('Error fetching rejected cases:', error);
    } finally {
      setLoadingRejectedCases(false);
    }
  };

  // Fetch accepted transfer cases
  const fetchAcceptedTransferCases = async () => {
    try {
      setLoadingTransferCases(true);
      const response = await axiosInstance.get('/ems/accepted-transfer-cases-overview', {
        params: {
          ems_team_status: filtersTransferCases.ems_team_status,
          date: filtersTransferCases.date,
        },
      });

      const apiData = Array.isArray(response.data) ? response.data : response.data?.data;
      setAcceptedTransferCases(apiData || []);
    } catch (error) {
      console.error('Error fetching accepted transfer cases:', error);
    } finally {
      setLoadingTransferCases(false);
    }
  };

  // Fetch hold transfer cases
  const fetchHoldTransferCases = async () => {
    try {
      setLoadingHoldTransferCases(true);
      const response = await axiosInstance.get('/ems/rejected-transfer-cases-overview', {
        params: {
          ems_team_status: filtersHoldTransferCases.ems_team_status,
          date: filtersHoldTransferCases.date,
        },
      });

      const apiData = Array.isArray(response.data) ? response.data : response.data?.data;
      setHoldTransferCases(apiData || []);
    } catch (error) {
      console.error('Error fetching hold transfer cases:', error);
    } finally {
      setLoadingHoldTransferCases(false);
    }
  };

  useEffect(() => {
    fetchActivityLogs();
  }, [filtersActivity.datetime]);

  useEffect(() => {
    fetchAlerts();
  }, [filtersAlerts.type, filtersAlerts.datetime]);

  useEffect(() => {
    fetchAcceptedCases();
  }, [filtersCases.ems_team_status, filtersCases.date]);

  useEffect(() => {
    fetchRejectedCases();
  }, [filtersRejectedCases.ems_team_status, filtersRejectedCases.date]);

  useEffect(() => {
    fetchAcceptedTransferCases();
  }, [filtersTransferCases.ems_team_status, filtersTransferCases.date]);

  useEffect(() => {
    fetchHoldTransferCases();
  }, [filtersHoldTransferCases.ems_team_status, filtersHoldTransferCases.date]);

  useEffect(() => {
    fetchResourceStatusHistory();
  }, [filtersResourceStatus.status, filtersResourceStatus.datetime, filtersResourceStatus.resource_name]);

  useEffect(() => {
    fetchCapacityUsage();
  }, [filtersCapacity.ems_team_status, filtersCapacity.date]);

  // Handle filter changes
  const handleActivityFilterChange = (e) => {
    const { name, value } = e.target;
    setFiltersActivity((prev) => ({ ...prev, [name]: value }));
    setPageActivity(1);
  };

  const handleAlertFilterChange = (e) => {
    const { name, value } = e.target;
    setFiltersAlerts((prev) => ({ ...prev, [name]: value }));
    setPageAlerts(1);
  };

  const handleCaseFilterChange = (e) => {
    const { name, value } = e.target;
    setFiltersCases((prev) => ({ ...prev, [name]: value }));
    setPageCases(1);
  };

  const handleRejectedCaseFilterChange = (e) => {
    const { name, value } = e.target;
    setFiltersRejectedCases((prev) => ({ ...prev, [name]: value }));
    setPageRejectedCases(1);
  };

  const handleTransferCaseFilterChange = (e) => {
    const { name, value } = e.target;
    setFiltersTransferCases((prev) => ({ ...prev, [name]: value }));
    setPageTransferCases(1);
  };

  const handleHoldTransferCaseFilterChange = (e) => {
    const { name, value } = e.target;
    setFiltersHoldTransferCases((prev) => ({ ...prev, [name]: value }));
    setPageHoldTransferCases(1);
  };

  const handleResourceStatusFilterChange = (e) => {
    const { name, value } = e.target;
    setFiltersResourceStatus((prev) => ({ ...prev, [name]: value }));
    setPageResourceStatus(1);
  };

  const handleCapacityFilterChange = (e) => {
    const { name, value } = e.target;
    setFiltersCapacity((prev) => ({ ...prev, [name]: value }));
    setPageCapacity(1);
  };

  // Clear filters
  const handleActivityClear = () => {
    setFiltersActivity({ datetime: '' });
    setPageActivity(1);
  };

  const handleAlertClear = () => {
    setFiltersAlerts({ type: '', datetime: '' });
    setPageAlerts(1);
  };

  const handleCaseClear = () => {
    setFiltersCases({ ems_team_status: '', date: '' });
    setPageCases(1);
  };

  const handleRejectedCaseClear = () => {
    setFiltersRejectedCases({ ems_team_status: '', date: '' });
    setPageRejectedCases(1);
  };

  const handleTransferCaseClear = () => {
    setFiltersTransferCases({ ems_team_status: '', date: '' });
    setPageTransferCases(1);
  };

  const handleHoldTransferCaseClear = () => {
    setFiltersHoldTransferCases({ ems_team_status: '', date: '' });
    setPageHoldTransferCases(1);
  };

  const handleResourceStatusClear = () => {
    setFiltersResourceStatus({ status: '', datetime: '', resource_name: '' });
    setPageResourceStatus(1);
  };

  const handleCapacityClear = () => {
    setFiltersCapacity({ ems_team_status: '', date: '' });
    setPageCapacity(1);
  };

  // Pagination
  const totalActivity = activityLogs.length;
  const paginatedActivityLogs = activityLogs.slice((pageActivity - 1) * limitActivity, pageActivity * limitActivity);

  const totalAlerts = alerts.length;
  const paginatedAlerts = alerts.slice((pageAlerts - 1) * limitAlerts, pageAlerts * limitAlerts);

  const totalCases = acceptedCases.length;
  const paginatedCases = acceptedCases.slice((pageCases - 1) * limitCases, pageCases * limitCases);

  const totalRejectedCases = rejectedCases.length;
  const paginatedRejectedCases = rejectedCases.slice((pageRejectedCases - 1) * limitRejectedCases, pageRejectedCases * limitRejectedCases);

  const totalTransferCases = acceptedTransferCases.length;
  const paginatedTransferCases = acceptedTransferCases.slice((pageTransferCases - 1) * limitTransferCases, pageTransferCases * limitTransferCases);

  const totalHoldTransferCases = holdTransferCases.length;
  const paginatedHoldTransferCases = holdTransferCases.slice((pageHoldTransferCases - 1) * limitHoldTransferCases, pageHoldTransferCases * limitHoldTransferCases);

  const totalResourceStatus = resourceStatusHistory.length;
  const paginatedResourceStatus = resourceStatusHistory.slice((pageResourceStatus - 1) * limitResourceStatus, pageResourceStatus * limitResourceStatus);

  const totalCapacity = capacityUsage.length;
  const paginatedCapacityUsage = capacityUsage.slice((pageCapacity - 1) * limitCapacity, pageCapacity * limitCapacity);

  const handleActivityPageChange = (newPage) => {
    setPageActivity(newPage);
  };

  const handleAlertPageChange = (newPage) => {
    setPageAlerts(newPage);
  };

  const handleCasePageChange = (newPage) => {
    setPageCases(newPage);
  };

  const handleRejectedCasePageChange = (newPage) => {
    setPageRejectedCases(newPage);
  };

  const handleTransferCasePageChange = (newPage) => {
    setPageTransferCases(newPage);
  };

  const handleHoldTransferCasePageChange = (newPage) => {
    setPageHoldTransferCases(newPage);
  };

  const handleResourceStatusPageChange = (newPage) => {
    setPageResourceStatus(newPage);
  };

  const handleCapacityPageChange = (newPage) => {
    setPageCapacity(newPage);
  };

  return (
    <>
      <style>{`
        .overview-table-panel .ros-table {height: 280px; overflow-y: auto;}
        .reports-analytics .filter-panel{align-items: flex-end;}
        .filter-panel{align-items: flex-end; justify-content: space-between !important; flex-wrap: wrap !important; display: flex;}
        .filter-panel .form-control{font-size: 12px;}
        .filter-panel .btn{font-size: 12px;}
        .reports-analytics .filter-panel form{width: 75%;}

        @media(max-width: 1499px) {
        .filter-panel .btn{font-size: 11px; padding: 7px !important}
        .filter-panel .form-control{font-size: 11px !important; padding: 7px !important;}
        }

        @media(max-width: 575px) {
          .filter-panel{flex-wrap: wrap !important;}
          .filter-left-panel {width: 100% !important;}
          .filter-left-panel .form-group{width: 100%;}
          .export-btn{width: 100%; }
          .export-btn .btn{width: 100% !important;}
        }
      `}</style>

      <div id="main-wrapper">
        <DateTimeFormatFetcher />
        <LeftSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
        <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
          <Topbar toggleSidebar={toggleSidebar} />
          <div className="body-wrapper">
            <div className="container-fluid">
              <Settings
                ref={settingsRef}
                updateSetting={updateSetting}
                handleColorTheme={handleColorTheme}
                settings={settings}
              />
              <h5 className="card-title fw-semibold mb-0 lh-sm">Reports & Analytics</h5>
              <p className="card-subtitle mb-0 mt-1">Insights and history of resource usage, cases, and system activities</p>

              <div className="row">
                <div className="col-lg-6 my-2">
                  <div className="card reports-analytics w-100 position-relative overflow-hidden">
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <h5 className="card-title mb-0 fw-semibold">Resource Utilization</h5>
                      </div>
                      <ResourceAreaChart />
                    </div>
                  </div>
                </div>

                <div className="col-lg-6 my-2">
                  <div className="card reports-analytics w-100 position-relative overflow-hidden">
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <h5 className="card-title mb-0 fw-semibold">Resource Availability History</h5>
                      </div>
                      <SpecialityAreaChart />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card reports-analytics w-100 position-relative overflow-hidden">
                <div className="card-body p-4">
                  <h5 className="card-title fw-semibold mb-0 lh-sm">Resource Activity Logs</h5>
                  <p className="card-subtitle mb-0 mt-1">View recent actions performed on resources across the system.</p>

                  <div className="filter-panel mt-2 mb-4">
                    <form className="col-md-9 d-flex flex-wrap filter-left-panel">
                      <div className="form-group col-md-4 col-sm-6 col-12 p-2">
                        <label>Date</label>
                        <input
                          type="date"
                          name="datetime"
                          value={filtersActivity.datetime}
                          onChange={handleActivityFilterChange}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group col-md-4 col-sm-6 col-12 p-2 d-flex align-items-end">
                        <button
                          type="button"
                          onClick={handleActivityClear}
                          className="btn w-100 waves-effect waves-light btn-rounded btn-outline-primary"
                        >
                          Clear
                        </button>
                      </div>
                    </form>
                    <div className="col-md-3 col-sm-6 col-12 p-2">
                      <div className="dropdown export-btn">
                        <button
                          className="btn btn-outline-primary dropdown-toggle w-100"
                          type="button"
                          id="exportDropdown"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          Export Options
                        </button>
                        <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => handleExport('activityLogs', 'pdf')}
                            >
                              Export to PDF
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => handleExport('activityLogs', 'csv')}
                            >
                              Export to CSV
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="py-3 px-0">
                    <div className="table-responsive rounded-2 ros-table">
                      <table className="table text-nowrap customize-table mb-0 align-middle activity-logs-table">
                        <thead className="text-dark fs-4">
                          <tr>
                            <th><h6 className="fw-semibold mb-0">Resource Name</h6></th>
                            <th><h6 className="fw-semibold mb-0">Action Performed</h6></th>
                            <th><h6 className="fw-semibold mb-0">Performed By</h6></th>
                            <th><h6 className="fw-semibold mb-0">Timestamp</h6></th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingActivity ? (
                            <tr>
                              <td colSpan="4" className="text-center">Loading...</td>
                            </tr>
                          ) : paginatedActivityLogs.length > 0 ? (
                            paginatedActivityLogs.map((log) => (
                              <tr key={log.id}>
                                <td><p className="mb-0 fw-normal">{log.resource_name}</p></td>
                                <td><p className="mb-0 fw-normal">{log.action_performed}</p></td>
                                <td><p className="mb-0 fw-normal">{log.performed_by}</p></td>
                                <td><p className="mb-0 fw-normal">{log.created_at ? formatCaseDateTime(log.created_at) : 'N/A'}</p></td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center">No activity logs found</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {totalActivity > 0 && (
                    <Pagination
                      total={totalActivity}
                      limit={limitActivity}
                      page={pageActivity}
                      onPageChange={handleActivityPageChange}
                    />
                  )}
                </div>
              </div>

              <ResourceStatusHistory onExport={handleExport} />

              <div className="card reports-analytics w-100 position-relative overflow-hidden">
                <div className="card-body p-4">
                  <h5 className="card-title fw-semibold mb-0 lh-sm">Alert History</h5>
                  <p className="card-subtitle mb-0 mt-1">Review all previously triggered system alerts with details.</p>

                  <div className="filter-panel mt-2 mb-4">
                    <form className="col-md-9 d-flex flex-wrap filter-left-panel">
                      <div className="form-group col-md-4 col-sm-6 col-12 p-2">
                        <label>Date</label>
                        <input
                          type="date"
                          name="datetime"
                          value={filtersAlerts.datetime}
                          onChange={handleAlertFilterChange}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group col-md-4 col-sm-6 col-12 p-2">
                        <label>Type</label>
                        <select
                          name="type"
                          value={filtersAlerts.type}
                          onChange={handleAlertFilterChange}
                          className="form-control form-select"
                        >
                          <option value="">All</option>
                          <option value="Employee Auto Assignment">Employee Auto Assignment</option>
                          <option value="Vehicle Checklist">Vehicle Checklist</option>
                          <option value="Vehicle Auto Assignment">Vehicle Auto Assignment</option>
                          <option value="Equipment Checklist">Equipment Checklist</option>
                          <option value="Case Acknowledgement Success">Case Acknowledgement Success</option>
                          <option value="Equipment Quantity limit">Equipment Quantity limit</option>
                        </select>
                      </div>
                      <div className="form-group col-md-4 col-sm-6 col-12 p-2 d-flex align-items-end">
                        <button
                          type="button"
                          onClick={handleAlertClear}
                          className="btn w-100 waves-effect waves-light btn-rounded btn-outline-primary"
                        >
                          Clear
                        </button>
                      </div>
                    </form>
                    <div className="col-md-3 col-sm-6 col-12 p-2">
                      <div className="dropdown export-btn">
                        <button
                          className="btn btn-outline-primary dropdown-toggle w-100"
                          type="button"
                          id="exportDropdown"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          Export Options
                        </button>
                        <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => handleExport('alerts', 'pdf')}
                            >
                              Export to PDF
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => handleExport('alerts', 'xml')}
                            >
                              Export to XML
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => handleExport('alerts', 'csv')}
                            >
                              Export to CSV
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="py-3 px-0">
                    <div className="table-responsive rounded-2 ros-table">
                      <table className="table text-nowrap customize-table mb-0 align-middle alerts-table">
                        <thead className="text-dark fs-4">
                          <tr>
                            <th><h6 className="fw-semibold mb-0">Alert Type</h6></th>
                            <th><h6 className="fw-semibold mb-0">Resource Affected</h6></th>
                            <th><h6 className="fw-semibold mb-0">Severity</h6></th>
                            <th><h6 className="fw-semibold mb-0">Date & Time</h6></th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingAlerts ? (
                            <tr>
                              <td colSpan="4" className="text-center">Loading...</td>
                            </tr>
                          ) : paginatedAlerts.length > 0 ? (
                            paginatedAlerts.map((alert) => (
                              <tr key={alert.id}>
                                <td><p className="mb-0 fw-normal">{alert.module_name}</p></td>
                                <td><p className="mb-0 fw-normal">{alert.description}</p></td>
                                <td>
                                  <p className="mb-0 fw-normal">
                                    <span
                                      className={`round-8 me-2 d-inline-block rounded-circle ${
                                        alert.severity === 'High'
                                          ? 'text-bg-danger'
                                          : alert.severity === 'Medium'
                                          ? 'text-bg-warning'
                                          : 'text-bg-success'
                                      }`}
                                    ></span>
                                    {alert.severity}
                                  </p>
                                </td>
                                <td><p className="mb-0 fw-normal">{alert.created_at ? formatCaseDateTime(alert.created_at) : 'N/A'}</p></td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center">No alerts found</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {totalAlerts > 0 && (
                    <Pagination
                      total={totalAlerts}
                      limit={limitAlerts}
                      page={pageAlerts}
                      onPageChange={handleAlertPageChange}
                    />
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-lg-6 mb-4">
                  <div className="card reports-analytics w-100 position-relative overflow-hidden mb-0 h-100">
                    <div className="card-body p-4">
                      <h5 className="card-title fw-semibold mb-0 lh-sm">Accepted Cases Overview</h5>
                      <p className="card-subtitle mb-0 mt-1">Visualize accepted case trends and track individual case assignments.</p>

                      <div className="filter-panel mt-2 mb-4">
                        <form className="col-md-9 d-flex flex-wrap filter-left-panel">
                          <div className="form-group col-md-4 col-sm-6 col-12 p-2">
                            <label>Date</label>
                            <input
                              type="date"
                              name="date"
                              value={filtersCases.date}
                              onChange={handleCaseFilterChange}
                              className="form-control"
                            />
                          </div>
                          <div className="form-group col-md-4 col-sm-6 col-12 p-2">
                            <label>Status</label>
                            <select
                              name="ems_team_status"
                              value={filtersCases.ems_team_status}
                              onChange={handleCaseFilterChange}
                              className="form-control form-select"
                            >
                              <option value="">All</option>
                              <option value="1">Assigned</option>
                              <option value="5">In Progress</option>
                              <option value="7">Completed</option>
                            </select>
                          </div>
                          <div className="form-group col-md-4 col-sm-6 col-12 p-2 d-flex align-items-end">
                            <button
                              type="button"
                              onClick={handleCaseClear}
                              className="btn w-100 waves-effect waves-light btn-rounded btn-outline-primary"
                            >
                              Clear
                            </button>
                          </div>
                        </form>

                        <div className="col-md-3 col-sm-6 col-12 p-2">
                          <div className="dropdown export-btn">
                            <button
                              className="btn btn-outline-primary dropdown-toggle w-100"
                              type="button"
                              id="exportDropdown"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              Export Options
                            </button>
                            <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('acceptedCases', 'pdf')}
                                >
                                  Export to PDF
                                </button>
                              </li>
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('acceptedCases', 'xml')}
                                >
                                  Export to XML
                                </button>
                              </li>
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('acceptedCases', 'csv')}
                                >
                                  Export to CSV
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="py-3 px-0 overview-table-panel">
                        <div className="table-responsive rounded-2 ros-table">
                          <table className="table text-nowrap customize-table mb-0 align-middle accepted-cases-table">
                            <thead className="text-dark fs-4">
                              <tr>
                                <th><h6 className="fw-semibold mb-0">Case ID</h6></th>
                                <th><h6 className="fw-semibold mb-0">Case Name</h6></th>
                                <th><h6 className="fw-semibold mb-0">Assigned Date</h6></th>
                                <th><h6 className="fw-semibold mb-0">Status</h6></th>
                              </tr>
                            </thead>
                            <tbody>
                              {loadingCases ? (
                                <tr>
                                  <td colSpan="4" className="text-center">Loading...</td>
                                </tr>
                              ) : paginatedCases.length > 0 ? (
                                paginatedCases.map((caseItem) => (
                                  <tr key={caseItem.id}>
                                    <td><p className="mb-0 fw-normal">{caseItem.id}</p></td>
                                    <td><p className="mb-0 fw-normal">{caseItem.case_name}</p></td>
                                    <td><p className="mb-0 fw-normal">{caseItem.created_at ? formatCaseDateTime(caseItem.created_at) : 'N/A'}</p></td>
                                    <td>
                                      <p className="mb-0 fw-normal">
                                        {caseItem.ems_team_status === 0 || caseItem.ems_team_status === 1
                                          ? 'Assigned'
                                          : [2, 3, 4, 5, 6].includes(caseItem.ems_team_status)
                                          ? 'In Progress'
                                          : caseItem.ems_team_status === 7
                                          ? 'Completed'
                                          : 'N/A'}
                                      </p>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="4" className="text-center">No cases found</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {totalCases > 0 && (
                        <Pagination
                          total={totalCases}
                          limit={limitCases}
                          page={pageCases}
                          onPageChange={handleCasePageChange}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-6 mb-4">
                  <div className="card reports-analytics w-100 position-relative overflow-hidden mb-0 h-100">
                    <div className="card-body p-4">
                      <h5 className="card-title fw-semibold mb-0 lh-sm">Hold Cases Overview</h5>
                      <p className="card-subtitle mb-0 mt-1">Monitor hold case trends and identify resource constraints or reasons for rejection.</p>

                      <div className="filter-panel mt-2 mb-4">
                        <form className="col-md-9 d-flex flex-wrap filter-left-panel">
                          <div className="form-group col-md-4 col-sm-6 col-12 p-2">
                            <label>Date</label>
                            <input
                              type="date"
                              name="date"
                              value={filtersRejectedCases.date}
                              onChange={handleRejectedCaseFilterChange}
                              className="form-control"
                            />
                          </div>
                          <div className="form-group col-md-4 col-sm-6 col-12 p-2 d-flex align-items-end">
                            <button
                              type="button"
                              onClick={handleRejectedCaseClear}
                              className="btn w-100 waves-effect waves-light btn-rounded btn-outline-primary"
                            >
                              Clear
                            </button>
                          </div>
                        </form>

                        <div className="col-md-3 col-sm-6 col-12 p-2">
                          <div className="dropdown export-btn">
                            <button
                              className="btn btn-outline-primary dropdown-toggle w-100"
                              type="button"
                              id="exportDropdown"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              Export Options
                            </button>
                            <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('rejectedCases', 'pdf')}
                                >
                                  Export to PDF
                                </button>
                              </li>
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('rejectedCases', 'xml')}
                                >
                                  Export to XML
                                </button>
                              </li>
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('rejectedCases', 'csv')}
                                >
                                  Export to CSV
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="py-3 px-0 overview-table-panel">
                        <div className="table-responsive rounded-2 ros-table">
                          <table className="table text-nowrap customize-table mb-0 align-middle rejected-cases-table">
                            <thead className="text-dark fs-4">
                              <tr>
                                <th><h6 className="fw-semibold mb-0">Case ID</h6></th>
                                <th><h6 className="fw-semibold mb-0">Case Name</h6></th>
                                <th><h6 className="fw-semibold mb-0">Assigned Date</h6></th>
                                <th><h6 className="fw-semibold mb-0">Status</h6></th>
                              </tr>
                            </thead>
                            <tbody>
                              {loadingRejectedCases ? (
                                <tr>
                                  <td colSpan="4" className="text-center">Loading...</td>
                                </tr>
                              ) : paginatedRejectedCases.length > 0 ? (
                                paginatedRejectedCases.map((caseItem) => (
                                  <tr key={caseItem.id}>
                                    <td><p className="mb-0 fw-normal">{caseItem.id}</p></td>
                                    <td><p className="mb-0 fw-normal">{caseItem.case_name}</p></td>
                                    <td><p className="mb-0 fw-normal">{caseItem.created_at ? formatCaseDateTime(caseItem.created_at) : 'N/A'}</p></td>
                                    <td>
                                      <p className="mb-0 fw-normal">
                                        {caseItem.ems_team_status === 0 ? 'On Hold' : 'Unknown'}
                                      </p>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="4" className="text-center">No rejected cases found</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {totalRejectedCases > 0 && (
                        <Pagination
                          total={totalRejectedCases}
                          limit={limitRejectedCases}
                          page={pageRejectedCases}
                          onPageChange={handleRejectedCasePageChange}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-6 mb-4">
                  <div className="card reports-analytics w-100 position-relative overflow-hidden mb-0 h-100">
                    <div className="card-body p-4">
                      <h5 className="card-title fw-semibold mb-0 lh-sm">Accepted Transfer Cases</h5>
                      <p className="card-subtitle mb-0 mt-1">Browse Accepted EMS Transfer cases with filter and export options.</p>
                      <div className="filter-panel mt-2 ">
                        <form className="col-md-9 d-flex flex-wrap filter-left-panel">
                          <div className="form-group col-md-4 col-sm-6 col-12 p-2">
                            <label>Date</label>
                            <input
                              type="date"
                              name="date"
                              value={filtersTransferCases.date}
                              onChange={handleTransferCaseFilterChange}
                              className="form-control"
                            />
                          </div>
                          <div className="form-group col-md-4 col-sm-6 col-12 p-2">
                            <label>Status</label>
                            <select
                              name="ems_team_status"
                              value={filtersTransferCases.ems_team_status}
                              onChange={handleTransferCaseFilterChange}
                              className="form-control form-select"
                            >
                              <option value="">All</option>
                              <option value="1">Assigned</option>
                              <option value="5">In Progress</option>
                              <option value="7">Completed</option>
                            </select>
                          </div>
                          <div className="form-group col-md-4 col-sm-6 col-12 p-2 d-flex align-items-end">
                            <button
                              type="button"
                              onClick={handleTransferCaseClear}
                              className="btn w-100 waves-effect waves-light btn-rounded btn-outline-primary"
                            >
                              Clear
                            </button>
                          </div>
                        </form>

                        <div className="col-md-3 col-sm-6 col-12 p-2">
                          <div className="dropdown export-btn">
                            <button
                              className="btn btn-outline-primary dropdown-toggle w-100"
                              type="button"
                              id="exportDropdown"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              Export Options
                            </button>
                            <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('acceptedTransferCases', 'pdf')}
                                >
                                  Export to PDF
                                </button>
                              </li>
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('acceptedTransferCases', 'xml')}
                                >
                                  Export to XML
                                </button>
                              </li>
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('acceptedTransferCases', 'csv')}
                                >
                                  Export to CSV
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="py-3 px-0 overview-table-panel">
                        <div className="table-responsive rounded-2 ros-table">
                          <table className="table text-nowrap customize-table mb-0 align-middle accepted-transfer-cases-table">
                            <thead className="text-dark fs-4">
                              <tr>
                                <th><h6 className="fw-semibold mb-0">Case ID</h6></th>
                                <th><h6 className="fw-semibold mb-0">Case Name</h6></th>
                                <th><h6 className="fw-semibold mb-0">Assigned Date</h6></th>
                                <th><h6 className="fw-semibold mb-0">Status</h6></th>
                              </tr>
                            </thead>
                            <tbody>
                              {loadingTransferCases ? (
                                <tr>
                                  <td colSpan="4" className="text-center">Loading...</td>
                                </tr>
                              ) : paginatedTransferCases.length > 0 ? (
                                paginatedTransferCases.map((caseItem) => (
                                  <tr key={caseItem.id}>
                                    <td><p className="mb-0 fw-normal">{caseItem.id}</p></td>
                                    <td><p className="mb-0 fw-normal">{caseItem.case_name}</p></td>
                                    <td><p className="mb-0 fw-normal">{caseItem.created_at ? formatCaseDateTime(caseItem.created_at) : 'N/A'}</p></td>
                                    <td>
                                      <p className="mb-0 fw-normal">
                                        {caseItem.ems_team_status === 0 || caseItem.ems_team_status === 1
                                          ? 'Assigned'
                                          : [2, 3, 4, 5, 6].includes(caseItem.ems_team_status)
                                          ? 'In Progress'
                                          : caseItem.ems_team_status === 7
                                          ? 'Completed'
                                          : 'N/A'}
                                      </p>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="4" className="text-center">No transfer cases found</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {totalTransferCases > 0 && (
                        <Pagination
                          total={totalTransferCases}
                          limit={limitTransferCases}
                          page={pageTransferCases}
                          onPageChange={handleTransferCasePageChange}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-6 mb-4">
                  <div className="card reports-analytics w-100 position-relative overflow-hidden mb-0 h-100">
                    <div className="card-body p-4">
                      <h5 className="card-title fw-semibold mb-0 lh-sm">Hold Transfer Cases</h5>
                      <p className="card-subtitle mb-0 mt-1">View hold transfer cases with filter and export options.</p>

                      <div className="filter-panel mt-2 mb-4">
                        <form className="col-md-9 d-flex flex-wrap filter-left-panel">
                          <div className="form-group col-md-4 col-sm-6 col-12 p-2">
                            <label>Date</label>
                            <input
                              type="date"
                              name="date"
                              value={filtersHoldTransferCases.date}
                              onChange={handleHoldTransferCaseFilterChange}
                              className="form-control"
                            />
                          </div>
                          <div className="form-group col-md-4 col-sm-6 col-12 p-2 d-flex align-items-end">
                            <button
                              type="button"
                              onClick={handleHoldTransferCaseClear}
                              className="btn w-100 waves-effect waves-light btn-rounded btn-outline-primary"
                            >
                              Clear
                            </button>
                          </div>
                        </form>

                        <div className="col-md-3 col-sm-6 col-12 p-2">
                          <div className="dropdown export-btn">
                            <button
                              className="btn btn-outline-primary dropdown-toggle w-100"
                              type="button"
                              id="exportDropdown"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              Export Options
                            </button>
                            <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('holdTransferCases', 'pdf')}
                                >
                                  Export to PDF
                                </button>
                              </li>
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('holdTransferCases', 'xml')}
                                >
                                  Export to XML
                                </button>
                              </li>
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('holdTransferCases', 'csv')}
                                >
                                  Export to CSV
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="py-3 px-0 overview-table-panel">
                        <div className="table-responsive rounded-2 ros-table">
                          <table className="table text-nowrap customize-table mb-0 align-middle hold-transfer-cases-table">
                            <thead className="text-dark fs-4">
                              <tr>
                                <th><h6 className="fw-semibold mb-0">Case ID</h6></th>
                                <th><h6 className="fw-semibold mb-0">Case Name</h6></th>
                                <th><h6 className="fw-semibold mb-0">Assigned Date</h6></th>
                                <th><h6 className="fw-semibold mb-0">Status</h6></th>
                              </tr>
                            </thead>
                            <tbody>
                              {loadingHoldTransferCases ? (
                                <tr>
                                  <td colSpan="4" className="text-center">Loading...</td>
                                </tr>
                              ) : paginatedHoldTransferCases.length > 0 ? (
                                paginatedHoldTransferCases.map((caseItem) => (
                                  <tr key={caseItem.id}>
                                    <td><p className="mb-0 fw-normal">{caseItem.id}</p></td>
                                    <td><p className="mb-0 fw-normal">{caseItem.case_name}</p></td>
                                    <td><p className="mb-0 fw-normal">{caseItem.created_at ? formatCaseDateTime(caseItem.created_at) : 'N/A'}</p></td>
                                    <td>
                                      <p className="mb-0 fw-normal">
                                        {caseItem.transfer_status === 0 ? 'On Hold' : 'Unknown'}
                                      </p>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="4" className="text-center">No hold transfer cases found</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {totalHoldTransferCases > 0 && (
                        <Pagination
                          total={totalHoldTransferCases}
                          limit={limitHoldTransferCases}
                          page={pageHoldTransferCases}
                          onPageChange={handleHoldTransferCasePageChange}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="row">
                        <div className="col-md-8">
                          <h5 className="card-title fw-semibold mb-0 lh-sm">Capacity Usage Per Time</h5>
                          <p className="card-subtitle mb-0 mt-1">Track employee and vehicle usage over time.</p>
                        </div>
                      </div>

                      <div className="filter-panel mt-2 mb-4">
                        <form className="col-md-9 d-flex flex-wrap filter-left-panel">
                          <div className="form-group col-md-4 col-sm-6 col-12 p-2">
                            <label>Date</label>
                            <input
                              type="date"
                              name="date"
                              value={filtersCapacity.date}
                              onChange={handleCapacityFilterChange}
                              className="form-control"
                            />
                          </div>
                          <div className="form-group col-md-4 col-sm-6 col-12 p-2 d-flex align-items-end">
                            <button
                              type="button"
                              onClick={handleCapacityClear}
                              className="btn w-100 waves-effect waves-light btn-rounded btn-outline-primary"
                            >
                              Clear
                            </button>
                          </div>
                        </form>

                        <div className="col-md-3 p-2">
                          <div className="dropdown export-btn">
                            <button
                              className="btn btn-outline-primary dropdown-toggle w-100"
                              type="button"
                              id="exportDropdown"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              Export Options
                            </button>
                            <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('capacityUsage', 'pdf')}
                                >
                                  Export to PDF
                                </button>
                              </li>
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('capacityUsage', 'xml')}
                                >
                                  Export to XML
                                </button>
                              </li>
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleExport('capacityUsage', 'csv')}
                                >
                                  Export to CSV
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="py-3 px-0 overview-table-panel">
                        <div className="table-responsive rounded-2 ros-table">
                          <table className="table text-nowrap customize-table mb-0 align-middle capacity-usage-table">
                            <thead className="text-dark fs-4">
                              <tr>
                                <th><h6 className="fw-semibold mb-0">Date</h6></th>
                                <th><h6 className="fw-semibold mb-0">Employee Usage (%)</h6></th>
                                <th><h6 className="fw-semibold mb-0">Vehicle Usage (%)</h6></th>
                              </tr>
                            </thead>
                            <tbody>
                              {loadingCapacity ? (
                                <tr>
                                  <td colSpan="3" className="text-center">Loading...</td>
                                </tr>
                              ) : paginatedCapacityUsage.length > 0 ? (
                                paginatedCapacityUsage.map((item, index) => (
                                  <tr key={index}>
                                    <td><p className="mb-0 fw-normal">{item.date ? formatCaseDateOnly(item.date) : 'N/A'}</p></td>
                                    <td><p className="mb-0 fw-normal">{item.employee_usage}</p></td>
                                    <td><p className="mb-0 fw-normal">{item.vehicle_usage}</p></td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="3" className="text-center">No capacity usage data found</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {totalCapacity > 0 && (
                        <Pagination
                          total={totalCapacity}
                          limit={limitCapacity}
                          page={pageCapacity}
                          onPageChange={handleCapacityPageChange}
                        />
                      )}
                    </div>
                    <div className="col-md-6 mt-md-0 mt-4">
                      <div className="row reports-analytics">
                        <div className="col-md-12">
                          <h5 className="card-title fw-semibold mb-0 lh-sm">Quick Overview</h5>
                          <p className="card-subtitle mb-0 mt-1">Track employee and vehicle counts over time.</p>
                        </div>
                        <EmployeeAssetUsageChart />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EMSReportsAnalytics;